import React, { memo, useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
// import { store } from 'react-notifications-component';
import * as API from '../../../services/api';
import { Form, FormGroup, FormText, FormFeedback, Row, Col, Label, Input } from 'reactstrap';
import { isEmpty } from 'lodash';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Notification } from '../../../utils';

const Switchery = memo((props) => (
  <div className="switcher">
    <input
      checked={props.checked}
      onChange={props.onChange}
      type="checkbox"
      name={props.for}
      id={props.for}
    />
    <label htmlFor={props.for}></label>
  </div>
));

const schemaIssuerInformation = yup.object().shape({
  username: yup.string().required('Username is required'),
  ap_first_name: yup.string().required('Authorize person first name is required'),
  ap_last_name: yup.string().required('Authorize person last name is required'),
  company_name: yup.string().required('Company name is required'),
  industry: yup.string().required('Industry type is required'),
  entity_type: yup.number(),
  no_of_employees: yup
    .number()
    .typeError('You must specify a number only')
    .positive('Must be greater than zero')
    .required('Number of employees is required'),
  tin: yup.string().required('TIN is required'),
  is_registered_dti: yup.boolean(),
  registered_dti_no: yup.string().when('is_registered_dti', {
    is: true,
    then: yup.string().required('DTI Registered Number is required')
  }),
  is_registered_sec: yup.boolean(),
  registered_sec_no: yup.string().when('is_registered_sec', {
    is: true,
    then: yup.string().required('SEC Registered Number is required')
  }),
  is_registered_other_agency: yup.boolean(),
  registered_other_agency: yup.string().when('is_registered_other_agency', {
    is: true,
    then: yup.string().required('Agency Name is required')
  }),
  pp_first_name_1: yup.string().when('entity_type', {
    is: (entityValue) => entityValue < 3,
    then: yup.string().required('First name is required')
  }),
  pp_last_name_1: yup.string().when('entity_type', {
    is: (entityValue) => entityValue < 3,
    then: yup.string().required('Last name is required')
  }),
  pp_civil_status_1: yup.string().when('entity_type', {
    is: (entityValue) => entityValue < 3,
    then: yup.string().required('Civil Status is required')
  }),
  address_line_1: yup.string().required('Business address is required'),
  country: yup.string().required('Country is required'),
  province: yup.string().required('Province is required'),
  city: yup.string().required('City is required'),
  zipcode: yup
    .number()
    .positive('Must be greater than zero')
    .required('Postal/Zip Code is required'),
  mobile_code: yup.string().required('Mobile code is required'),
  mobile_number: yup
    .number()
    .positive('Must be greater than zero')
    .min(10)
    .required('Mobile number is required'),
  email: yup.string().email('Must be a valid email').required('Email is required')
});

const FormIssuerInformationEdit = (props) => {
  const { issuer } = props;

  const {
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    reset,
    control
  } = useForm({
    defaultValues: { entity_type: 1, business_size: 1 },
    resolver: yupResolver(schemaIssuerInformation)
  });

  const entityValue = watch('entity_type', 1);
  const registeredMunicipality = watch('is_registered_municipality', false);
  const registeredDTI = watch('is_registered_dti', false);
  const registeredSEC = watch('is_registered_sec', false);
  const registeredAgency = watch('is_registered_other_agency', false);

  const [optionCountry, setOptionCountry] = useState([]);
  const [optionCountryCodes, setOptionCountryCodes] = useState([]);
  const [optionCivil, setCivilStatus] = useState([]);
  const [optionBusinessEntity, setOptionBusinessEntity] = useState([]);
  const [optionBusinessSize, setOptionBusinessSize] = useState([]);
  const [optionIndustry, setOptionIndustry] = useState([]);

  useEffect(() => {
    API.dataSets([
      'countries_id',
      'country_codes',
      'business_entities',
      'withdrawal_type',
      'civil_status',
      'business_size',
      'industries'
    ]).then((res) => {
      setOptionIndustry(res.data.industries);
      setOptionBusinessEntity(res.data.business_entities);
      setOptionCountry(res.data.countries_id);
      setOptionCountryCodes(res.data.country_codes);
      setOptionBusinessSize(res.data.business_size);
      setCivilStatus(res.data.civil_status);
    });
  }, []);

  useEffect(() => {
    const { company_details } = issuer;
    issuer &&
      issuer.company_details &&
      reset({
        email: issuer.email,
        mobile_number: issuer.mobile_number,
        mobile_code: issuer.mobile_code,
        address_line_1: company_details.address_line_1,
        ap_first_name: issuer.first_name,
        ap_last_name: issuer.last_name,
        ap_middle_name: issuer.middle_name || '',
        ap_suffix_name: issuer.suffix_name || '',
        business_size: company_details.business_size && company_details.business_size.id,
        city: company_details.city,
        company_name: company_details.company_name,
        country: company_details.country,
        email: issuer.email,
        entity_type: company_details.entity_type && company_details.entity_type.id,
        fax_code: company_details.fax_code || '',
        fax_number: company_details.fax_number || '',
        industry: company_details.industry,
        is_registered_dti: company_details.is_registered_dti == 1,
        is_registered_municipality: company_details.is_registered_municipality == 1,
        is_registered_other_agency: company_details.is_registered_other_agency == 1,
        is_registered_sec: company_details.is_registered_sec == 1,
        landline_number: company_details.landline_number || '',
        mobile_code: issuer.mobile_code,
        mobile_number: issuer.mobile_number,
        no_of_employees: company_details.no_of_employees,
        pp_civil_status_1:
          company_details.pp_civil_status_1 && company_details.pp_civil_status_1.id,
        pp_civil_status_2:
          company_details.pp_civil_status_2 && company_details.pp_civil_status_2.id,
        province: company_details.province,
        registered_dti_no: company_details.registered_dti_no || '',
        registered_other_agency: company_details.registered_other_agency || '',
        registered_sec_no: company_details.registered_sec_no || '',
        social_media: company_details.social_media || '',
        tin: company_details.tin,
        username: issuer.username,
        website: company_details.website || '',
        zipcode: company_details.zipcode || ''
      });
  }, [issuer, issuer.company_details]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [editable, setEditable] = useState(false);
  const enableEdit = () => {
    setEditable(!editable);
  };

  const onStepOneSubmit = (data) => {
    setLoadingSubmit(true);

    const paramsObj = {
      ...data,
      ap_middle_name: data.ap_middle_name || '',
      ap_suffix_name: data.ap_suffix_name || '',
      pp_civil_status_1: data.pp_civil_status_1 || '',
      pp_civil_status_2: data.pp_civil_status_2 || '',
      no_of_employees: String(data.no_of_employees),
      is_registered_dti: data.is_registered_dti ? 1 : 0,
      is_registered_municipality: data.is_registered_municipality ? 1 : 0,
      is_registered_other_agency: data.is_registered_other_agency ? 1 : 0,
      is_registered_sec: data.is_registered_sec ? 1 : 0
    };

    const params = new URLSearchParams();
    for (let [key, value] of Object.entries(paramsObj)) {
      params.append(key, value);
    }

    API.updateIssuer(issuer.id, params)
      .then((response) => {
        setLoadingSubmit(false);
        setEditable(false);
      })
      .catch((err) => {
        setLoadingSubmit(false);
        if (err.response.status === 422) {
          for (const [key, value] of Object.entries(err.response.data.errors)) {
            setError(key, {
              type: 'manual',
              message: value
            });
          }
        } else {
          Notification.error(err);
        }
      })
      .finally(() => {
        setLoadingSubmit(false);
      });
  };

  return (
    <Form onSubmit={handleSubmit(onStepOneSubmit)}>
      {/* <FormGroup tag="fieldset">
        <h3>Authorized Person</h3>
        <FormGroup row>
          <Label md="3">Username</Label>
          <Col md="9">
            <Controller
              name="username"
              defaultValue=""
              control={control}
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.username)} />
              )}
            />
            <FormFeedback style={{ display: errors.username ? 'block' : 'none' }}>
              {errors.username?.message}
            </FormFeedback>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">Password</Label>
          <Col md="9">
            <strong>Password will be auto-generated</strong>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">First Name</Label>
          <Col md="9">
            <Controller
              name="ap_first_name"
              defaultValue=""
              control={control}
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.ap_first_name)} />
              )}
            />
            <FormFeedback style={{ display: errors.ap_first_name ? 'block' : 'none' }}>
              {errors.ap_first_name?.message}
            </FormFeedback>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">Last Name</Label>
          <Col md="9">
            <Controller
              name="ap_last_name"
              defaultValue=""
              control={control}
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.ap_last_name)} />
              )}
            />
            <FormFeedback style={{ display: errors.ap_last_name ? 'block' : 'none' }}>
              {errors.ap_last_name?.message}
            </FormFeedback>
          </Col>
        </FormGroup>
      </FormGroup>
      <hr /> */}
      <FormGroup tag="fieldset" disabled={!editable}>
        <h3>Issuer Information</h3>
        <FormGroup row>
          <Label md="3">Company Name</Label>
          <Col md="9">
            <Controller
              control={control}
              defaultValue=""
              name="company_name"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.company_name)} />
              )}
            />
            <FormFeedback style={{ display: errors.company_name ? 'block' : 'none' }}>
              {errors.company_name?.message}
            </FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Business Entity</Label>
          <Col md="9">
            <Controller
              name="entity_type"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  style={{ container: { borderColor: '#f44336' } }}
                  isSearchable={false}
                  value={optionBusinessEntity.find((c) => c.value === value)}
                  onChange={(val) => onChange(val.value)}
                  options={optionBusinessEntity}
                  isOptionDisabled={(option) => option.disabled}
                />
              )}
            />
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Business Size</Label>
          <Col md="9">
            <Controller
              name="business_size"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  isSearchable={false}
                  value={optionBusinessSize.find((c) => c.value === value)}
                  onChange={(val) => onChange(val.value)}
                  options={optionBusinessSize}
                  isOptionDisabled={(option) => option.disabled}
                />
              )}
            />
            <FormFeedback style={{ display: errors.business_size ? 'block' : 'none' }}>
              {errors.business_size?.message}
            </FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Business Industry</Label>
          <Col md="9">
            <Controller
              name="industry"
              isClearable
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  value={optionIndustry.find((c) => c.value === value)}
                  onChange={(val) => onChange(val.value)}
                  options={optionIndustry}
                  isOptionDisabled={(option) => option.disabled}
                />
              )}
            />
            <FormFeedback style={{ display: errors.industry ? 'block' : 'none' }}>
              {errors.industry?.message}
            </FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Total Number of Employees</Label>
          <Col md="9">
            <Controller
              control={control}
              name="no_of_employees"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  className="number-only-field"
                  invalid={!isEmpty(errors.no_of_employees)}
                />
              )}
            />
            {errors.no_of_employees && (
              <FormFeedback>{errors.no_of_employees?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">TIN</Label>

          <Col md="9">
            <Controller
              control={control}
              name="tin"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  className="number-only-field"
                  invalid={!isEmpty(errors.tin)}
                />
              )}
            />
            {errors.tin && <FormFeedback>{errors.tin?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Registered at the municipality</Label>
          <Col md="9" className="px-2 d-flex align-items-center">
            <Controller
              control={control}
              name="is_registered_municipality"
              // defaultValue={true}
              render={({ field: { value, onChange } }) => (
                <Switchery
                  checked={value}
                  onChange={onChange}
                  disabled
                  for="is_registered_municipality"
                />
              )}
            />
          </Col>
          {/* <Col md="8">
            <Controller
              control={control}
              name="tin"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.tin)} placeholder="TIN" />
              )}
            />
            {errors.tin && <FormFeedback>{errors.tin?.message}</FormFeedback>}
          </Col> */}
        </FormGroup>

        <FormGroup row>
          <Label md="3">Registered at the DTI</Label>
          <Col md="1" className="px-2 d-flex align-items-center">
            <Controller
              control={control}
              name="is_registered_dti"
              render={({ field: { value, onChange } }) => (
                <Switchery checked={value} onChange={onChange} for="is_registered_dti" />
              )}
            />
          </Col>
          <Col md="8">
            <Controller
              control={control}
              name="registered_dti_no"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  invalid={!isEmpty(errors.registered_dti_no)}
                  placeholder="DTI Registration No."
                  disabled={!registeredDTI}
                />
              )}
            />
            {errors.registered_dti_no && (
              <FormFeedback>{errors.registered_dti_no?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Registered at the SEC</Label>
          <Col md="1" className="px-2 d-flex align-items-center">
            <Controller
              control={control}
              name="is_registered_sec"
              render={({ field: { value, onChange } }) => (
                <Switchery checked={value} onChange={onChange} for="is_registered_sec" />
              )}
            />
          </Col>
          <Col md="8">
            <Controller
              control={control}
              name="registered_sec_no"
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={!registeredSEC}
                  type="text"
                  invalid={!isEmpty(errors.registered_sec_no)}
                  placeholder="SEC Registration No."
                />
              )}
            />
            {errors.registered_sec_no && (
              <FormFeedback>{errors.registered_sec_no?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Registered at other agency</Label>
          <Col md="1" className="px-2 d-flex align-items-center">
            <Controller
              control={control}
              name="is_registered_other_agency"
              render={({ field: { value, onChange } }) => (
                <Switchery checked={value} onChange={onChange} for="is_registered_other_agency" />
              )}
            />
          </Col>
          <Col md="8">
            <Controller
              control={control}
              name="registered_other_agency"
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  disabled={!registeredAgency}
                  invalid={!isEmpty(errors.registered_other_agency)}
                  placeholder="Agency Name"
                />
              )}
            />
            {errors.registered_other_agency && (
              <FormFeedback>{errors.registered_other_agency?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>
      </FormGroup>
      <hr />
      {entityValue < 3 && (
        <>
          <FormGroup tag="fieldset" disabled={!editable}>
            <h3>For Single Proprietorship and Partnership</h3>
            <Row>
              <Col>
                <FormGroup row>
                  <Label md="3">First Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_first_name_1"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_first_name_1)} />
                      )}
                    />
                    {errors.pp_first_name_1 && (
                      <FormFeedback>{errors.pp_first_name_1?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Middle Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_middle_name_1"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_middle_name_1)} />
                      )}
                    />
                    {errors.pp_middle_name_1 && (
                      <FormFeedback>{errors.pp_middle_name_1?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Last Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_last_name_1"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_last_name_1)} />
                      )}
                    />
                    {errors.pp_last_name_1 && (
                      <FormFeedback>{errors.pp_last_name_1?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Civil Status</Label>
                  <Col md="9">
                    <Controller
                      name="pp_civil_status_1"
                      isClearable
                      control={control}
                      render={({ field: { onChange, value, ref } }) => (
                        <ReactSelect
                          inputRef={ref}
                          value={optionCivil.find((c) => c.value === value)}
                          onChange={(val) => onChange(val.value)}
                          options={optionCivil}
                          isOptionDisabled={(option) => option.disabled}
                        />
                      )}
                    />
                    <FormFeedback style={{ display: errors.pp_civil_status_1 ? 'block' : 'none' }}>
                      {errors.pp_civil_status_1?.message}
                    </FormFeedback>
                  </Col>
                </FormGroup>
              </Col>
              <Col>
                <FormGroup row>
                  <Label md="3">First Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_first_name_2"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_first_name_2)} />
                      )}
                    />
                    {errors.pp_first_name_2 && (
                      <FormFeedback>{errors.pp_first_name_2?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Middle Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_middle_name_2"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_middle_name_2)} />
                      )}
                    />
                    {errors.pp_middle_name_2 && (
                      <FormFeedback>{errors.pp_middle_name_2?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Last Name</Label>
                  <Col md="9">
                    <Controller
                      control={control}
                      name="pp_last_name_2"
                      render={({ field }) => (
                        <Input {...field} type="text" invalid={!isEmpty(errors.pp_last_name_2)} />
                      )}
                    />
                    {errors.pp_last_name_2 && (
                      <FormFeedback>{errors.pp_last_name_2?.message}</FormFeedback>
                    )}
                  </Col>
                </FormGroup>
                <FormGroup row>
                  <Label md="3">Civil Status</Label>
                  <Col md="9">
                    <Controller
                      name="pp_civil_status_2"
                      isClearable
                      control={control}
                      render={({ field: { onChange, value, ref } }) => (
                        <ReactSelect
                          inputRef={ref}
                          value={optionCivil.find((c) => c.value === value)}
                          onChange={(val) => onChange(val.value)}
                          options={optionCivil}
                          isOptionDisabled={(option) => option.disabled}
                        />
                      )}
                    />
                  </Col>
                </FormGroup>
              </Col>
            </Row>
            {/* <Row>
              <Col>
                <button className="btn btn-primary float-right" type="button">
                  <i className="fa fa-plus" style={{ marginRight: 10 }} />
                  Add More
                </button>
              </Col>
            </Row> */}
          </FormGroup>
          <hr />
        </>
      )}

      <FormGroup tag="fieldset" disabled={!editable}>
        <h3>Business Contact Information</h3>

        <FormGroup row>
          <Label md="3">Address</Label>
          <Col md="9">
            <Controller
              control={control}
              name="address_line_1"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.address_line_1)} />
              )}
            />
            {errors.address_line_1 && <FormFeedback>{errors.address_line_1?.message}</FormFeedback>}
            <FormText>(Apartment, unit, suite, or floor #, Street Name, Municipality)</FormText>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Country</Label>
          <Col md="9">
            <Controller
              name="country"
              isClearable
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  value={optionCountry.find((c) => c.value === value)}
                  onChange={(val) => onChange(val.value)}
                  options={optionCountry}
                  isOptionDisabled={(option) => option.disabled}
                />
              )}
            />
            <FormFeedback style={{ display: errors.country ? 'block' : 'none' }}>
              {errors.country?.message}
            </FormFeedback>
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Province</Label>
          <Col md="9">
            <Controller
              name="province"
              isClearable
              control={control}
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.province)} />
              )}
            />
            <FormFeedback style={{ display: errors.province ? 'block' : 'none' }}>
              {errors.province?.message}
            </FormFeedback>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">City</Label>
          <Col md="9">
            <Controller
              name="city"
              isClearable
              control={control}
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.city)} />
              )}
            />
            <FormFeedback style={{ display: errors.city ? 'block' : 'none' }}>
              {errors.city?.message}
            </FormFeedback>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label md="3">Postal/Zip Code</Label>
          <Col md="9">
            <Controller
              control={control}
              name="zipcode"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  className="number-only-field"
                  invalid={!isEmpty(errors.zipcode)}
                />
              )}
            />
            {errors.zipcode && <FormFeedback>{errors.zipcode?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Landline</Label>
          <Col md="9">
            <Controller
              control={control}
              name="landline_number"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  className="number-only-field"
                  invalid={!isEmpty(errors.landline_number)}
                />
              )}
            />
            {errors.landline_number && (
              <FormFeedback>{errors.landline_number?.message}</FormFeedback>
            )}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Fax Number</Label>
          <Col md="3">
            <Controller
              name="fax_code"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  className="number-only-field"
                  invalid={!isEmpty(errors.fax_code)}
                  placeholder="Area Code"
                />
              )}
            />
            {errors.fax_code && <FormFeedback>{errors.fax_code?.message}</FormFeedback>}
          </Col>
          <Col md="6">
            <Controller
              control={control}
              name="fax_number"
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  invalid={!isEmpty(errors.fax_number)}
                  className="number-only-field"
                />
              )}
            />
            {errors.fax_number && <FormFeedback>{errors.fax_number?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Mobile Number</Label>
          <Col md="3">
            <Controller
              name="mobile_code"
              defaultValue="+63"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  value={optionCountryCodes.find((c) => c.value === value)}
                  onChange={(val) => onChange(val.value)}
                  options={optionCountryCodes}
                  isOptionDisabled={(option) => option.disabled}
                />
              )}
            />
            {errors.mobile_code && <FormFeedback>{errors.mobile_code?.message}</FormFeedback>}
          </Col>
          <Col md="6">
            <Controller
              control={control}
              name="mobile_number"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.mobile_number)} />
              )}
            />
            {errors.mobile_number && <FormFeedback>{errors.mobile_number?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Email</Label>
          <Col md="9">
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.email)} />
              )}
            />
            {errors.email && <FormFeedback>{errors.email?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Website</Label>
          <Col md="9">
            <Controller
              control={control}
              name="website"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.website)} />
              )}
            />
            {errors.website && <FormFeedback>{errors.website?.message}</FormFeedback>}
          </Col>
        </FormGroup>

        <FormGroup row>
          <Label md="3">Social Media Linke</Label>
          <Col md="9">
            <Controller
              control={control}
              name="social_media"
              render={({ field }) => (
                <Input {...field} type="text" invalid={!isEmpty(errors.social_media)} />
              )}
            />
            {errors.social_media && <FormFeedback>{errors.social_media?.message}</FormFeedback>}
          </Col>
        </FormGroup>
      </FormGroup>

      <Row>
        <Col>
          {!editable ? (
            <a type="button" onClick={enableEdit} className={`btn btn-primary float-right`}>
              Edit
            </a>
          ) : (
            <button
              type="submit"
              className={`btn ${loadingSubmit ? 'btn-gray' : 'btn-primary'} float-right`}
              disabled={loadingSubmit}
            >
              {loadingSubmit ? <i className="fas fa-spinner fa-spin"></i> : 'Save'}
            </button>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default withRouter(FormIssuerInformationEdit);
